import React, { useEffect, useState, Fragment, useMemo, useCallback } from 'react';
import { Dialog, DialogTitle, Transition, DialogBackdrop, DialogPanel, Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import { TrashIcon, XMarkIcon, ClockIcon, UserIcon, UserPlusIcon, CheckIcon, ChevronUpDownIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import axios from '../../axios';
import { selectStyles } from '../../Utils/selectStyles';
import Table from '../UI/Table';
import { Checkbox } from '@headlessui/react';
import  notify  from '../../Utils/notify';

const EditCitaModal = ({ open, onClose, citaId, citas, setCitas, appointments_clean }) => {
    const [cita, setCita] = useState({ 
        id: 0, 
        paciente: '', 
        telefono: '', 
        email: '',
        observaciones: '', 
        doctor: '', 
        products: [], 
        excedido: 0,
        patient_id: null,
        source: '',
        nombre_agendo: ''
    });
    const [productos, setProductos] = useState([]);
    const [patients, setPatients] = useState([]);
    const [excedente, setExcedente] = useState(0);
    const [citaProducts, setCitaProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [successMsg, setSuccessMsg] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [isUnregisteredPatient, setIsUnregisteredPatient] = useState(true); // Siempre activo - solo pacientes manuales
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [pacienteEsAgendador, setPacienteEsAgendador] = useState(false);
    const [codigoPais, setCodigoPais] = useState('+52'); // México por defecto
    const [forceUpdate, setForceUpdate] = useState(false); // Checkbox para forzar actualización
    const [toleranciaSucursal, setToleranciaSucursal] = useState(0);

    // Códigos de país comunes
    const codigosPais = [
        { code: '+52', country: 'México' },
        { code: '+1', country: 'USA/Canadá' },
        { code: '+34', country: 'España' },
        { code: '+54', country: 'Argentina' },
        { code: '+56', country: 'Chile' },
        { code: '+57', country: 'Colombia' },
        { code: '+51', country: 'Perú' },
        { code: '+58', country: 'Venezuela' },
        { code: '+593', country: 'Ecuador' },
        { code: '+591', country: 'Bolivia' },
        { code: '+595', country: 'Paraguay' },
        { code: '+598', country: 'Uruguay' },
        { code: '+503', country: 'El Salvador' },
        { code: '+502', country: 'Guatemala' },
        { code: '+504', country: 'Honduras' },
        { code: '+505', country: 'Nicaragua' },
        { code: '+506', country: 'Costa Rica' },
        { code: '+507', country: 'Panamá' },
    ];

    useEffect(() => {
        if (citaId && open) {
            axios.get(`api/appointments/${citaId}`).then(resp => {
                setCita(resp.data);
                
                // Cargar tolerancia de la sucursal
                if (resp.data.sucursal_id) {
                    axios.get(`api/sucursals/${resp.data.sucursal_id}`).then(sucursalResp => {
                        setToleranciaSucursal(sucursalResp.data.tolerancia_minutos || 0);
                    }).catch(e => {
                        console.error('Error cargando sucursal:', e);
                        setToleranciaSucursal(0);
                    });
                }
                
                // Detectar código de país del teléfono existente
                if (resp.data.telefono_paciente) {
                    const telefono = resp.data.telefono_paciente;
                    const codigoEncontrado = codigosPais.find(c => telefono.startsWith(c.code));
                    if (codigoEncontrado) {
                        setCodigoPais(codigoEncontrado.code);
                        // Remover el código del teléfono para mostrarlo limpio
                        const numeroSinCodigo = telefono.substring(codigoEncontrado.code.length).trim();
                        setCita(prev => ({ ...prev, telefono_paciente: numeroSinCodigo }));
                    }
                }
                if (resp.data.products) {
                    let tempProds = resp.data.products.map(p => p.id);
                    setCitaProducts(tempProds);
                }
                if (resp.data.excedido > 0) setExcedente(resp.data.excedido);
                
                // Modo solo pacientes manuales - siempre no registrado
                setIsUnregisteredPatient(true);
                setSelectedPatient(null);
                
                getProductos();
                getPatients();
            }).catch(e => console.log("Error: ", e));
        }
    }, [citaId, open]);

    const getProductos = () => {
        axios.get('api/products').then((resp) => {
            setProductos(resp.data);
        });
    };

    const getPatients = () => {
        axios.get('api/patients').then((resp) => {
            setPatients(resp.data);
        }).catch(e => console.log("Error: ", e));
    };

    // Opciones para React Select de pacientes
    const patientOptions = useMemo(() => {
        return patients.map(p => ({
            value: p.id,
            label: `${p.nombre} - ${p.celular || p.telefono || 'Sin teléfono'}`,
            patient: p
        }));
    }, [patients]);

    // Manejar selección de paciente
    const handlePatientSelect = (option) => {
        setSelectedPatient(option);
        if (option) {
            setCita(prev => ({
                ...prev,
                patient_id: option.value,
                paciente: option.patient.nombre,
                nombre_paciente: option.patient.nombre,
                telefono_paciente: option.patient.celular || option.patient.telefono || '',
            }));
        } else {
            setCita(prev => ({
                ...prev,
                patient_id: null,
                paciente: '',
                nombre_paciente: '',
                telefono_paciente: ''
            }));
        }
    };

    // Toggle deshabilitado - siempre en modo manual
    const handleUnregisteredToggle = (checked) => {
        // Función deshabilitada - el checkbox siempre estará en true
        return;
    };

    const _closeModal = () => {
        setCita({ id: null, paciente: '', telefono_paciente: '', email: '', observaciones: '', doctor: '', products: [], patient_id: null, nombre_agendo: '' });
        setExcedente(0);
        setCitaProducts([]);
        setSuccessMsg('');
        setErrorMsg('');
        setIsUnregisteredPatient(true); // Mantener siempre en true
        setSelectedPatient(null);
        setCodigoPais('+52'); // Resetear a México
        setForceUpdate(false); // Resetear checkbox de forzar
        onClose();
    };

    const clearFormValues = () => {
        setCita(prev => ({
            ...prev,
            nombre_paciente: '',
            telefono_paciente: '',
            email: '',
            observaciones: '',
            doctor: '',
            products: [],
            patient_id: null,
            nombre_agendo: ''
        }));
        setCitaProducts([]);
        setSelectedPatient(null);
        setForceUpdate(false);
        setTimeout(() => {
            setExcedente(0);
            setLoading(false);
            setSuccessMsg('Cita limpiada correctamente');
        }, 400);
    };

    // Validación del formulario
    const validateForm = () => {
        const nombrePaciente = cita.nombre_paciente || cita.paciente || '';
        const telefonoPaciente = cita.telefono_paciente || '';

        if (!nombrePaciente.trim()) {
            setErrorMsg('El nombre del paciente es requerido');
            return false;
        }
        if (!telefonoPaciente.trim()) {
            setErrorMsg('El teléfono del paciente es requerido');
            return false;
        }
        setErrorMsg('');
        return true;
    };

    // Verificar si hay servicios seleccionados
    const hasNoServices = citaProducts.length === 0;

    const _updateCita = async () => {
        // Validar campos requeridos
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        
        try {
            // Validar que no sea día festivo (solo si hay paciente y productos)
            if (cita.nombre_paciente && citaProducts.length > 0) {
                const validacionFestivo = await axios.post('api/holidays/verificar', {
                    fecha: cita.fecha,
                    sucursal_id: cita.sucursal_id
                });

                if (validacionFestivo.data.es_festivo) {
                    const festivoNombre = validacionFestivo.data.festivo?.nombre || 'Día festivo';
                    notify.error(`No se pueden agendar citas en días festivos: ${festivoNombre}`);
                    setLoading(false);
                    return;
                }
            }

            //asegurar de que cita.products en todos los casos sea un array de IDs
            // Concatenar código de país con teléfono
            const telefonoCompleto = cita.telefono_paciente 
                ? `${codigoPais}${cita.telefono_paciente}`.trim()
                : '';

            const updatedCita = {
                ...cita,
                products: citaProducts,
                source: cita.source || 'Backoffice',
                // Siempre paciente manual - patient_id siempre null
                patient_id: null,
                // Siempre enviar nombre_paciente y telefono_paciente
                nombre_paciente: cita.nombre_paciente || cita.paciente || '',
                telefono_paciente: telefonoCompleto,
                email: cita.email || '', // Email opcional
                force_update: forceUpdate, // Enviar flag de forzar actualización
            };
          
            const resp = await axios.put(`api/appointments/${cita.id}`, updatedCita);
 
            setCita(resp.data);
            if (resp.status === 200) {
                notify.success('Cita actualizada correctamente');
                setCitas(citas.map(c => (c.id === resp.data.id) ? resp.data : c));
                _closeModal();
            }
            setLoading(false);
        } catch (e) {
            console.log("Error: ", e);
            setLoading(false);
            
            // Manejar error 409 (conflicto de reserva)
            if (e.response?.status === 409) {
                const errorData = e.response.data;
                notify.error(
                    errorData.message || 'Esta cita ya fue ocupada por otro usuario',
                    { duration: 4000 }
                );
                
                // Auto-actualizar la cita con datos frescos
                if (errorData.cita) {
                    setCita(errorData.cita);
                    setCitas(citas.map(c => (c.id === errorData.cita.id) ? errorData.cita : c));
                }
                
                // Cerrar modal después de mostrar error
                setTimeout(() => {
                    _closeModal();
                }, 4000);
            } else {
                notify.error('Error al actualizar la cita');
            }
        }
    };

    const _cleanCita = () => {
        if (window.confirm("¿Estás seguro de limpiar esta cita?, si la guardas, se eliminarán todos los datos del paciente y estudios asignados.")) {
            setLoading(true);
           /* axios.put(`api/appointments/${cita.id}`, {
                paciente: '',
                fecha: cita.fecha,
                hora: cita.hora,
                telefono: '',
                observaciones: '',
                sucursal_id: cita.sucursal_id,
                doctor: '',
                products: [],
                excedido: 0
            }).then(resp => {
                setCita(resp.data);
                if (resp.status === 200) {
                    setSuccessMsg('Cita limpiada correctamente');
                    setTimeout(() => {
                        setCitas(citas.map(c => (c.id === resp.data.id) ? resp.data : c));
                        clearFormValues();
                    }, 2000);
                }
                setLoading(false);
            }).catch(e => {
                console.log("Error: ", e);
                setLoading(false);
            });*/
            clearFormValues();
        }
    };

    // Función centralizada para calcular el excedente de tiempo
    const calcularExcedente = useCallback((estudiosIds) => {
        if (estudiosIds.length === 0 || !cita.hora || !cita.fecha) {
            return 0;
        }

        // Obtener productos completos
        let estudioTemp = [];
        estudiosIds.forEach(elem => {
            const prod = productos.find(p => p.id === elem);
            if (prod) estudioTemp.push(prod);
        });

        if (estudioTemp.length === 0) {
            return 0;
        }

        // Calcular duración del slot actual
        let tempCitas = citas.filter((f) => f.fecha === cita.fecha);
        let citaActualIndex = tempCitas.findIndex(t => t.id === parseInt(cita.id));

        if (tempCitas.length > 0 && citaActualIndex !== -1 && citaActualIndex + 1 < tempCitas.length) {
            let citaCurrentTime = new Date(cita.fecha + ":" + cita.hora);
            let nextCitaTime = new Date(tempCitas[citaActualIndex + 1].fecha + ":" + tempCitas[citaActualIndex + 1].hora);
            
            let diffMs = (nextCitaTime - citaCurrentTime);
            let diffMins = Math.round(((diffMs % 86400000) % 3600000) / 60000);
            
            // Calcular duración acumulada de estudios
            let duracion_acumulada = 0;
            estudioTemp.forEach((item) => duracion_acumulada += Number(item.duracion_aproximada));
            
            // Aplicar tolerancia de la sucursal
            let tiempoDisponible = diffMins + toleranciaSucursal;
            let excedenteMins = duracion_acumulada - tiempoDisponible;
            
            return excedenteMins > 0 ? excedenteMins : 0;
        }

        return 0;
    }, [cita.hora, cita.fecha, cita.id, productos, citas, toleranciaSucursal]);

    const _handleEstudio = (estudiosIds) => {
        if (estudiosIds.length > 0) {
            // Calcular excedente usando la función centralizada
            const excedenteMins = calcularExcedente(estudiosIds);
            
            if (excedenteMins > 0) {
                let msg = "El estudio seleccionado para esta cita se excede con " + excedenteMins + " minutos, ¿estás seguro de agregarlo?";
                if (window.confirm(msg)) {
                    setCita({ ...cita, products: estudiosIds, excedido: excedenteMins });
                    setCitaProducts(estudiosIds);
                    setExcedente(excedenteMins);
                } else {
                    // Usuario canceló, no actualizar
                    return;
                }
            } else {
                setCita({ ...cita, products: estudiosIds, excedido: 0 });
                setCitaProducts(estudiosIds);
                setExcedente(0);
            }
        } else {
            setCita({ ...cita, products: estudiosIds, excedido: 0 });
            setCitaProducts(estudiosIds);
            setExcedente(0);
        }
    };

    const selectedProductos = productos.filter(p => citaProducts.includes(p.id));

    // País seleccionado para el selector
    const paisSeleccionado = useMemo(() => {
        return codigosPais.find(p => p.code === codigoPais) || codigosPais[0];
    }, [codigoPais, codigosPais]);

    // Opciones formateadas para react-select
    const productosOptions = useMemo(() => {
        return productos.map(p => ({
            value: p.id,
            label: `${p.nombre_producto} (${p.duracion_aproximada} min)`,
            duracion: p.duracion_aproximada
        }));
    }, [productos]);

    // Valores seleccionados para react-select
    const selectedOptions = useMemo(() => {
        return productosOptions.filter(opt => citaProducts.includes(opt.value));
    }, [productosOptions, citaProducts]);

    // Handler para cambios en select (mantiene la misma lógica de validación)
    const handleProductosChange = (selected) => {
        const ids = selected ? selected.map(s => s.value) : [];
        _handleEstudio(ids);
    };

    // Calcular duración total de servicios seleccionados
    const totalDuracion = useMemo(() => {
        return selectedProductos.reduce((acc, p) => acc + (Number(p.duracion_aproximada) || 0), 0);
    }, [selectedProductos]);

    // Recalcular excedente automáticamente cuando cambien los productos seleccionados
    useEffect(() => {
        if (citaProducts.length > 0 && productos.length > 0 && cita.hora && cita.fecha) {
            const excedenteMins = calcularExcedente(citaProducts);
            setExcedente(excedenteMins);
        } else {
            setExcedente(0);
        }
    }, [citaProducts, productos, cita.hora, cita.fecha, cita.id, citas, calcularExcedente]);

    return (
        <Transition show={open} as={Fragment}>
            <Dialog onClose={()=>void 0} className="fixed z-50 inset-0 overflow-y-auto">
                <div className="flex items-center justify-center min-h-screen px-4">
                    <DialogBackdrop className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                    <DialogPanel className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-auto z-50 relative">
                        {/* Header */}
                        <div className="flex justify-between items-center border-b bg-linear-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-lg">
                            <DialogTitle className="text-lg font-semibold text-white m-2">Editar Cita</DialogTitle>
                            <button onClick={_closeModal} className="text-white hover:text-gray-200">
                                <XMarkIcon className="h-6 w-6" />
                            </button>
                        </div>
                        {/* Body */}
                        <div className="px-6 py-4 grid grid-cols-12 gap-4 max-h-[70vh] overflow-y-auto">
                            
                            {/* Información de última actualización */}
                            {cita.updated_by && cita.updated_at && (
                                <div className="col-span-12 bg-gray-50 border border-gray-200 rounded-lg p-3">
                                    <div className="flex items-center justify-between text-xs text-gray-600">
                                        <div className="flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                                            </svg>
                                            <span>
                                                <strong>Última actualización:</strong> {cita.updated_by?.name || 'Usuario desconocido'}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span>
                                                {new Date(cita.updated_at).toLocaleString('es-ES', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Checkbox paciente no registrado - Siempre activo */}
                            <div className="col-span-12 flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                                <Checkbox
                                    checked={true}
                                    onChange={handleUnregisteredToggle}
                                    disabled={true}
                                    className="h-7 w-7 text-blue-600 border-gray-400 rounded focus:ring-blue-500 opacity-60 cursor-not-allowed"
                                    id="unregistered-patient"
                                >
                                    {({ checked }) => (
                                        <span
                                            className="flex h-4 w-4 rounded border border-gray-300 mr-2 bg-blue-600 items-center justify-center opacity-60"
                                        >
                                            <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l4 4L15 7" />
                                            </svg>
                                        </span>
                                    )}
                                </Checkbox>
                                <label htmlFor="unregistered-patient" className="flex items-center gap-2 text-sm text-blue-700 cursor-not-allowed">
                                    <UserPlusIcon className="h-5 w-5 text-blue-500" />
                                    <span className="font-medium">Paciente no registrado (agregar manualmente)</span>
                                </label>
                            </div>

                            {/* Sección de paciente registrado (React Select) */}
                            {!isUnregisteredPatient && (
                                <div className="col-span-12">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        <UserIcon className="h-4 w-4 inline mr-1" />
                                        Buscar Paciente
                                    </label>
                                    <Select
                                        value={selectedPatient}
                                        onChange={handlePatientSelect}
                                        options={patientOptions}
                                        placeholder="Buscar paciente..."
                                        isClearable
                                        isSearchable
                                        noOptionsMessage={() => "No se encontraron pacientes"}
                                        styles={selectStyles}
                                        classNamePrefix="react-select"
                                    />
                                    {selectedPatient && (
                                        <p className="mt-1 text-xs text-green-600">
                                            Paciente seleccionado: {selectedPatient.label}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Sección de paciente no registrado */}
                            {isUnregisteredPatient && (
                                <>
                                    <div className="col-span-12 md:col-span-6">
                                        <label htmlFor="paciente" className="block text-sm font-medium text-gray-700 mb-1">
                                            Nombre del paciente
                                        </label>
                                        <input
                                            id="paciente"
                                            type="text"
                                            className="w-full dento-input"
                                            value={cita.nombre_paciente || ''}
                                            onChange={(e) => setCita({ ...cita, paciente: e.target.value, nombre_paciente: e.target.value })}
                                            placeholder="Nombre completo del paciente"
                                        />
                                    </div>
                                    
                                    <div className="col-span-12 md:col-span-6">
                                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                            Email <span className="text-xs text-gray-500">(opcional)</span>
                                        </label>
                                        <input
                                            id="email"
                                            type="email"
                                            className="w-full dento-input"
                                            value={cita.email || ''}
                                            onChange={(e) => setCita({ ...cita, email: e.target.value })}
                                            placeholder="correo@ejemplo.com"
                                        />
                                    </div>
                                </>
                            )}
                            <div className="col-span-12 md:col-span-6 flex justify-start items-center gap-4">
                                <div className="flex justify-center gap-2 mb-1">
                                    <Checkbox
                                        checked={pacienteEsAgendador}
                                        onChange={(checked) => {
                                            setPacienteEsAgendador(checked);
                                            if (checked) {
                                                setCita(prev => ({
                                                    ...prev,
                                                    nombre_agendo: prev.paciente || ''
                                                }));
                                            } else {
                                                setCita(prev => ({
                                                    ...prev,
                                                    nombre_agendo: ''
                                                }));
                                            }
                                        }}
                                        className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                        id="paciente-es-agendador"
                                    >
                                        {({ checked }) => (
                                            <span
                                                className={`inline-block h-4 w-4 rounded border border-gray-300 mr-2 ${checked ? 'bg-blue-600' : 'bg-white'} flex items-center justify-center transition-colors`}
                                            >
                                                {checked && (
                                                    <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l4 4L15 7" />
                                                    </svg>
                                                )}
                                            </span>
                                        )}
                                    </Checkbox>
                                    <label htmlFor="paciente-es-agendador" className="text-sm text-gray-700 cursor-pointer select-none">
                                        El paciente es quien agenda la cita
                                    </label>
                                </div>
                            </div>

                            {/* Nombre del agendador y checkbox */}
                            {!pacienteEsAgendador && (
                            <div className="col-span-12 md:col-span-6">
                                <label htmlFor="nombre_agendo" className="block text-sm font-medium text-gray-700 mb-1">
                                    Nombre del agendador
                                </label>
                                <input
                                    id="nombre_agendo"
                                    type="text"
                                    className="w-full dento-input"
                                    value={cita.nombre_agendo || ''}
                                    onChange={(e) => setCita({ ...cita, nombre_agendo: e.target.value })}
                                    placeholder="Nombre de quien agenda la cita"
                                    disabled={pacienteEsAgendador}
                                />
                            </div>
                            )}
                          
                          
                             {/* Teléfono con código de país */}
                            <div className="col-span-12">
                                <label htmlFor="telefono_paciente" className="block text-sm font-medium text-gray-700 mb-1">Teléfono | Celular</label>
                                <div className="flex items-center  gap-2">
                                    {/* Selector de código de país - Headless UI Listbox */}
                                    <Listbox value={paisSeleccionado} onChange={(pais) => setCodigoPais(pais.code)}>
                                        <div className="relative w-40">
                                            <ListboxButton className="relative w-full border-2 border-cyan-400 rounded-md px-3 py-2.5 text-left text-sm font-medium text-gray-700 bg-white cursor-pointer hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none shadow-sm">
                                                <span className="block truncate">
                                                    {paisSeleccionado.code} {paisSeleccionado.country}
                                                </span>
                                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                                    <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                                                </span>
                                            </ListboxButton>
                                            <Transition
                                                as={Fragment}
                                                leave="transition ease-in duration-100"
                                                leaveFrom="opacity-100"
                                                leaveTo="opacity-0"
                                            >
                                                <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                    {codigosPais.map((pais) => (
                                                        <ListboxOption
                                                            key={pais.code}
                                                            value={pais}
                                                            className={({ active }) =>
                                                                `relative cursor-pointer select-none py-2 pl-7 pr-4 ${
                                                                    active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                                                                }`
                                                            }
                                                        >
                                                            {({ selected, active }) => (
                                                                <>
                                                                    <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                                                        {pais.code} - {pais.country}
                                                                    </span>
                                                                    {selected && (
                                                                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                                                            <CheckIcon className="h-5 w-5" />
                                                                        </span>
                                                                    )}
                                                                </>
                                                            )}
                                                        </ListboxOption>
                                                    ))}
                                                </ListboxOptions>
                                            </Transition>
                                        </div>
                                    </Listbox>
                                    {/* Input de teléfono */}
                                    <input
                                        id="telefono_paciente"
                                        type="tel"
                                        className="flex-1 dento-input"
                                        value={cita.telefono_paciente || ''}
                                        onChange={(e) => {
                                            const value = e.target.value.replace(/\D/g, '');
                                            setCita({ ...cita, telefono_paciente: value });
                                        }}
                                        placeholder="Número de teléfono"
                                    />
                                </div>
                            </div>
                            
                           
                            {/* Estudios */}
                            <div className="col-span-12">
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Estudios/Servicios
                                </label>
                                <Select
                                    isMulti
                                    options={productosOptions}
                                    value={selectedOptions}
                                    onChange={handleProductosChange}
                                    placeholder="Seleccionar estudios..."
                                    noOptionsMessage={() => "No hay estudios disponibles"}
                                    styles={selectStyles}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                />
                                {excedente > 0 && (
                                    <p className="mt-2 text-sm text-red-600 bg-red-50 rounded px-2 py-1 flex items-center gap-2">
                                        <ClockIcon className="h-4 w-4" />
                                        La duración de la cita se excede por {excedente} minutos
                                    </p>
                                )}
                            </div>

                            {/* Vista previa de servicios seleccionados */}
                            {selectedProductos.length > 0 && (
                                <div className="col-span-12">
                                    <div className="bg-linear-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                                        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                            <ClockIcon className="h-5 w-5 text-blue-500" />
                                            Resumen de Servicios
                                        </h4>
                                        <Table
                                            columns={[
                                                { 
                                                    key: 'nombre_producto', 
                                                    label: 'Servicio',
                                                    render: (value) => (
                                                        <span className="font-medium text-gray-800">{value}</span>
                                                    )
                                                },
                                                { 
                                                    key: 'duracion_aproximada', 
                                                    label: 'Duración',
                                                    align: 'right',
                                                    render: (value) => (
                                                        <span className="inline-flex items-center gap-1 text-gray-600">
                                                            <ClockIcon className="h-4 w-4" />
                                                            {value} min
                                                        </span>
                                                    )
                                                }
                                            ]}
                                            data={selectedProductos}
                                            keyExtractor={(row) => row.id}
                                            striped={true}
                                            hoverable={true}
                                            shadow={false}
                                            bordered={false}
                                            rounded={true}
                                            responsive={false}
                                            emptyMessage="No hay servicios seleccionados"
                                        />
                                        {/* Total de duración */}
                                        <div className="mt-3 pt-3 border-t-2 border-blue-200 flex justify-between items-center bg-blue-100/50 rounded-b-lg px-4 py-2">
                                            <span className="font-bold text-gray-800">Total</span>
                                            <span className="font-bold text-blue-700 flex items-center gap-1">
                                                <ClockIcon className="h-4 w-4" />
                                                {totalDuracion} min
                                                {totalDuracion >= 60 && (
                                                    <span className="text-xs text-gray-500 ml-1">
                                                        ({Math.floor(totalDuracion / 60)}h {totalDuracion % 60}m)
                                                    </span>
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Doctor */}
                            <div className="col-span-12 md:col-span-6">
                                <label htmlFor="doctor" className="block text-sm font-medium text-gray-700 mb-1">Doctor</label>
                                <input
                                    id="doctor"
                                    type="text"
                                    className="w-full dento-input"
                                    value={cita.doctor || ''}
                                    onChange={(e) => setCita({ ...cita, doctor: e.target.value })}
                                />
                            </div>
                            {/* Observaciones */}
                            <div className="col-span-12 md:col-span-12">
                                <label htmlFor="observaciones" className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                                <textarea
                                    id="observaciones"
                                    rows={8}
                                    style={{height: '6rem' }}
                                    className="w-full border-2 border-cyan-400 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                    value={cita.observaciones || ''}
                                    onChange={(e) => setCita({ ...cita, observaciones: e.target.value })}
                                />
                            </div>

                            {/* Alert de advertencia si no hay servicios seleccionados */}
                            {hasNoServices && (
                                <div className="col-span-12 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg px-4 py-3 text-sm flex items-center gap-2">
                                    <svg className="h-5 w-5 text-yellow-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                    <span><strong>Advertencia:</strong> No se ha seleccionado ningún servicio/estudio para esta cita.</span>
                                </div>
                            )}

                            {/* Mensaje de error */}
                            {errorMsg && (
                                <div className="col-span-12 bg-red-100 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm flex items-center gap-2">
                                    <svg className="h-5 w-5 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    {errorMsg}
                                </div>
                            )}

                            {/* Mensaje de éxito */}
                            {successMsg && (
                                <div className="col-span-12 bg-green-100 text-green-700 rounded px-3 py-2 text-sm flex items-center gap-2">
                                    <CheckIcon className="h-5 w-5" />
                                    {successMsg}
                                </div>
                            )}
                            {/* Botón limpiar */}
                            {appointments_clean && (
                                <div className="col-span-12 flex justify-end">
                                    <button
                                        type="button"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 transition-colors text-sm"
                                        onClick={_cleanCita}
                                        disabled={loading}
                                    >
                                        <TrashIcon className="h-5 w-5" />
                                        Limpiar cita
                                    </button>
                                </div>
                            )}
                        </div>
                        {/* Footer */}
                        <div className="bg-gray-50 px-6 py-4 rounded-b-lg">
                            {/* Checkbox para forzar actualización */}
                            <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                                <Checkbox
                                    checked={forceUpdate}
                                    onChange={setForceUpdate}
                                    className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500"
                                    id="force-update"
                                >
                                    {({ checked }) => (
                                        <span
                                            className={`inline-block h-4 w-4 rounded border border-gray-300 ${
                                                checked ? 'bg-orange-600' : 'bg-white'
                                            } flex items-center justify-center transition-colors`}
                                        >
                                            {checked && (
                                                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l4 4L15 7" />
                                                </svg>
                                            )}
                                        </span>
                                    )}
                                </Checkbox>
                                <label htmlFor="force-update" className="text-sm text-gray-700 cursor-pointer select-none">
                                    <span className="font-medium text-orange-600">Forzar actualización</span>
                                    <span className="text-gray-500"> (Modificar cita incluso si ya está ocupada)</span>
                                </label>
                            </div>
                            <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                                onClick={_closeModal}
                                disabled={loading}
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                onClick={_updateCita}
                                disabled={loading}
                            >
                                {loading ? 'Guardando...' : 'Guardar'}
                            </button>
                            </div>
                        </div>
                    </DialogPanel>
                </div>
            </Dialog>
        </Transition>
    );
};

export default EditCitaModal;